import React, { useState, useEffect, useRef, useMemo } from 'react';
import io from 'socket.io-client';
import api from '../lib/api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface InGameProps {
  match: any;
  onMatchFinished?: () => void | Promise<void>;
}

const InGame = ({ match, onMatchFinished }: InGameProps) => {
  const [score, setScore] = useState<Record<string, number>>(() =>
    match.score ? JSON.parse(match.score) : {}
  );
  const [lastGoalScorer, setLastGoalScorer] = useState<string | null>(null);
  const previousScoreRef = useRef<Record<string, number>>(score);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState('');

  const participants = useMemo(() => match.participants || [], [match.participants]);

  const leftParticipant = useMemo(
    () => participants.find((p: any) => p.side === 'left') || participants[0],
    [participants]
  );
  const rightParticipant = useMemo(
    () => participants.find((p: any) => p.side === 'right') || participants[1],
    [participants]
  );

  const leftKey = leftParticipant?.teamId || leftParticipant?.side || 'left';
  const rightKey = rightParticipant?.teamId || rightParticipant?.side || 'right';

  const anonymousLabels = {
    left: 'Modrý tým',
    right: 'Červený tým'
  };

  const leftName =
    leftParticipant?.team?.name ||
    (leftParticipant?.side === 'right' ? anonymousLabels.right : anonymousLabels.left);
  const rightName =
    rightParticipant?.team?.name ||
    (rightParticipant?.side === 'left' ? anonymousLabels.left : anonymousLabels.right);

  useEffect(() => {
    const parsed = match.score ? JSON.parse(match.score) : {};
    setScore(parsed);
    previousScoreRef.current = parsed;
  }, [match.score]);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
    });

    const joinMatchRoom = () => socket.emit('joinMatch', match.id);

    socket.on('connect', joinMatchRoom);
    joinMatchRoom();

    socket.on('goal', (newScore) => {
      const parsedScore = typeof newScore === 'string' ? JSON.parse(newScore) : (newScore || {});
      const prevScore = previousScoreRef.current || {};

      setScore({ ...parsedScore });
      previousScoreRef.current = parsedScore;

      const prevScore1 = prevScore[leftKey] || 0;
      const prevScore2 = prevScore[rightKey] || 0;
      const newScore1 = parsedScore[leftKey] || 0;
      const newScore2 = parsedScore[rightKey] || 0;

      if (newScore1 > prevScore1) {
        setLastGoalScorer(leftKey);
        setTimeout(() => setLastGoalScorer(null), 2000);
      } else if (newScore2 > prevScore2) {
        setLastGoalScorer(rightKey);
        setTimeout(() => setLastGoalScorer(null), 2000);
      }
    });

    socket.on('matchEnded', async () => {
      if (onMatchFinished) {
        await onMatchFinished();
      }
    });

    return () => {
      socket.off('goal');
      socket.off('connect', joinMatchRoom);
      socket.off('matchEnded');
      socket.disconnect();
    };
  }, [match.id, leftKey, rightKey, onMatchFinished]);

  const handleEndMatch = async () => {
    if (ending) return;
    setEndError('');

    const confirmed = window.confirm('Opravdu chcete ukončit tento zápas?');
    if (!confirmed) {
      return;
    }

    setEnding(true);
    try {
      await api.post(`/matches/${match.id}/end`);
      if (onMatchFinished) {
        await onMatchFinished();
      }
    } catch (err: any) {
      console.error('Failed to end match', err);
      setEndError(err.response?.data?.message || 'Zápas se nepodařilo ukončit. Zkuste to prosím znovu.');
    } finally {
      setEnding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6 sm:mb-8 animate-fade-in">
          <div className="text-4xl sm:text-6xl mb-3 animate-pulse-slow">⚽</div>
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Probíhající zápas
          </h2>
          <p className="text-gray-600 text-lg">Ať vyhraje ten nejlepší!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 animate-slide-up">
          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card-hover border border-white text-center">
            <div className="text-5xl sm:text-6xl mb-4">👤</div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 truncate px-2">{leftName}</h3>
            <div className={`text-6xl sm:text-8xl font-bold transition-all duration-300 ${lastGoalScorer === leftKey ? 'text-primary-600 scale-110' : 'text-gray-900'}`}>
              {score[leftKey] || 0}
            </div>
          </div>

          <div className="hidden md:flex flex-col items-center justify-center">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-8 rounded-3xl shadow-glow">
              <div className="text-5xl font-bold animate-pulse-slow">VS</div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card-hover border border-white text-center">
            <div className="text-5xl sm:text-6xl mb-4">👤</div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 truncate px-2">{rightName}</h3>
            <div className={`text-6xl sm:text-8xl font-bold transition-all duration-300 ${lastGoalScorer === rightKey ? 'text-accent-700 scale-110' : 'text-gray-900'}`}>
              {score[rightKey] || 0}
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col sm:flex-row justify-around items-center text-center gap-4">
            <div>
              <div className="text-2xl font-bold text-gray-900">{match.mode === 'individual' ? '1 vs 1' : 'Tým'}</div>
              <div className="text-sm text-gray-600">Režim zápasu</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-600">⚡ Živě</div>
              <div className="text-sm text-gray-600">Stav</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">První do 10</div>
              <div className="text-sm text-gray-600">Podmínka výhry</div>
            </div>
          </div>
        </div>

        {lastGoalScorer && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-2xl shadow-glow-lg animate-slide-up z-50">
            <div className="text-2xl font-bold">GÓL! 🎯</div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
          <button
            onClick={handleEndMatch}
            disabled={ending}
            className="w-full sm:w-auto px-6 py-3 font-bold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {ending ? 'Ukončuji zápas...' : 'Ukončit zápas'}
          </button>
          {endError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-xl w-full sm:w-auto">
              {endError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InGame;
